import { defineMessages, useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import * as React from 'react';
import { AlternateIndicator } from './AlternateIndicator';
import { SpecificityIndicator } from './SpecificityIndicator';

export const messages = defineMessages({
  impactMatch: {
    id: 'omicsExplorer.indicatorsLegend.gainLossFunction',
    defaultMessage: 'Gain/loss-of-function',
  },
  variantMatch: {
    id: 'omicsExplorer.indicatorsLegend.proteinEffect',
    defaultMessage: 'Protein Effect',
  },
  geneMatch: {
    id: 'omicsExplorer.indicatorsLegend.geneMatch',
    defaultMessage: 'Gene Match',
  },
  alternateVariant: {
    id: 'omicsExplorer.indicatorsLegend.alternateVariant',
    defaultMessage: 'Affects Alternate Transcript',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  },
  item: {
    display: 'grid',
    gridTemplateColumns: `${theme.pxToRem(12)} 1fr`,
    gap: theme.spacing(0.625),
    '& p': {
      lineHeight: theme.pxToRem(14),
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
}));

interface IndicatorLegendProps {
  className?: string;
  match?: 'gene' | 'variant';
}

export const IndicatorLegend: React.FC<IndicatorLegendProps> = ({
  className,
  match = 'gene',
  ...rootProps
}) => {
  const classes = useStyles({});

  const { formatMessage } = useIntl();

  return (
    <Box className={classes.root} {...rootProps}>
      <Tooltip title={formatMessage(messages.impactMatch)}>
        <Box align="center" className={classes.item}>
          <SpecificityIndicator match="impact" />
          <Text size="caption">{formatMessage(messages.impactMatch)}</Text>
        </Box>
      </Tooltip>
      <Tooltip title={formatMessage(messages.variantMatch)}>
        <Box align="center" className={classes.item}>
          <SpecificityIndicator match="variant" />
          <Text size="caption">{formatMessage(messages.variantMatch)}</Text>
        </Box>
      </Tooltip>
      <Tooltip title={formatMessage(messages.geneMatch)}>
        <Box align="center" className={classes.item}>
          <SpecificityIndicator match="gene" />
          <Text size="caption">{formatMessage(messages.geneMatch)}</Text>
        </Box>
      </Tooltip>
      <Tooltip title={formatMessage(messages.alternateVariant)}>
        <Box align="center" className={classes.item}>
          <AlternateIndicator />
          <Text size="caption">{formatMessage(messages.alternateVariant)}</Text>
        </Box>
      </Tooltip>
    </Box>
  );
};
